import * as React from 'react';
import PropTypes from 'prop-types';
import { withHistory } from 'slate-history';
import { createEditor } from 'slate';
import { Slate, Editable, withReact, useSlate } from 'slate-react';

import { withPastedHTML } from './utils';

const EditorWrapper = ({ plugins, initialValue, onChange, children }) => {
  const [editor] = React.useState(() => {
    const editor = withHistory(withReact(createEditor()));
    plugins?.forEach((plugin) => {
      plugin.with && plugin.with(editor);
    });
    return editor;
  });

  return (
    <Slate
      editor={editor}
      onChange={(newValue) => onChange(newValue)}
      value={initialValue}
    >
      {children}
    </Slate>
  );
};

const Editor = ({
  plugins = [],
  decorate: decorateList = [],
  renderElement: renderElementList = [],
  renderLeaf: renderLeafList = [],
  onDOMBeforeInput: onDOMBeforeInputList = [],
  onKeyDown: onKeyDownList = [],
  isPasteHtmlActive = false,
  ...props
}) => {
  const plainEditor = useSlate();
  const editor = isPasteHtmlActive ? withPastedHTML(plainEditor) : plainEditor;

  const decoratePlugins = (entry) => {
    let ranges = [];

    decorateList.forEach((decorate) => {
      const newRanges = decorate(entry) || [];
      if (newRanges.length) ranges = [...ranges, ...newRanges];
    });

    plugins.forEach((plugin) => {
      if (!plugin.decorate) return;

      const newRanges = plugin.decorate(entry) || [];
      if (newRanges.length) ranges = [...ranges, ...newRanges];
    });

    return ranges;
  };

  const onDOMBeforeInputPlugins = (event) => {
    onDOMBeforeInputList.forEach((onDOMBeforeInput) => {
      onDOMBeforeInput(event, editor);
    });

    plugins.forEach(({ onDOMBeforeInput }) => {
      if (!onDOMBeforeInput) return;
      onDOMBeforeInput(event, editor);
    });
  };

  const renderElementPlugins = React.useCallback(
    (elementProps) => {
      let element;

      renderElementList.some((renderElement) => {
        element = renderElement(elementProps);
        return !!element;
      });
      if (element) return element;

      plugins.some(({ renderElement }) => {
        element = renderElement && renderElement(elementProps);
        return !!element;
      });
      if (element) return element;

      return <div {...elementProps.attributes}>{elementProps.children}</div>;
    },
    [plugins]
  );

  const renderLeafPlugins = (leafProps) => {
    renderLeafList.forEach((renderLeaf) => {
      leafProps.children = renderLeaf(leafProps);
    });

    plugins.forEach(({ renderLeaf }) => {
      if (!renderLeaf) return;
      leafProps.children = renderLeaf(leafProps);
    });

    return <span {...leafProps.attributes}>{leafProps.children}</span>;
  };

  const onKeyDownPlugins = (event) => {
    onKeyDownList.forEach((onKeyDown) => {
      onKeyDown(event, editor);
    });

    plugins.forEach(({ onKeyDown }) => {
      if (!onKeyDown) return;
      onKeyDown(event, editor);
    });
  };

  return (
    <Editable
      {...props}
      decorate={decoratePlugins}
      onDOMBeforeInput={onDOMBeforeInputPlugins}
      renderElement={renderElementPlugins}
      renderLeaf={renderLeafPlugins}
      onKeyDown={onKeyDownPlugins}
    />
  );
};

EditorWrapper.propTypes = {
  initialValue: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.node,
  plugins: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func,
};

Editor.propTypes = {
  plugins: PropTypes.arrayOf(PropTypes.object),
  decorate: PropTypes.arrayOf(PropTypes.func),
  renderElement: PropTypes.arrayOf(PropTypes.func),
  renderLeaf: PropTypes.arrayOf(PropTypes.func),
  onDOMBeforeInput: PropTypes.arrayOf(PropTypes.func),
  onKeyDown: PropTypes.arrayOf(PropTypes.func),
  value: PropTypes.arrayOf(PropTypes.object),
  initialValue: PropTypes.arrayOf(PropTypes.object),
  isPasteHtmlActive: PropTypes.bool,
  children: PropTypes.node,
};

export { Editor, EditorWrapper };
